import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Shield,
  LogOut,
  Users,
  FileText,
  List,
  Settings,
  X,
  Code,
  Music,
  Building,
  LayoutGrid,
  Activity
} from "lucide-react";
import HamburgerButton from "../../Components/ui/HamburgerButton";
import logoImage from "../../assets/Logo/GIGiFY Logo Horizontal no tagline.png";
import { signOut } from "firebase/auth";
import { auth } from "../../Services/Firebase/FirebaseIndex";
import { getAdminPermissions } from "../AdminServices/AdminPermissionService";
import debug from "../../utils/debug";

// Icon imports
import IconDash from "../../assets/Icons/IconDash.png";
import IconCalendar from "../../assets/Icons/IconCalendar.png";
import IconContract from "../../assets/Icons/IconContract.png";
import IconAnalytics from "../../assets/Icons/IconAnalytics.png";
import IconDollar from "../../assets/Icons/IconDollar.png";
import IconSettings from "../../assets/Icons/IconSettings.png";
import IconNews from "../../assets/Icons/IconNews.png";

const AdminSidebar = ({ isMobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [availableMenuItems, setAvailableMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Simple toggle - no complex animations
  const handleButtonClick = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  
  // Define all possible menu items with their required permissions and icons
  const allMenuItems = [
    {
      icon: <img src={IconDash} alt="Dashboard" style={{ width: "30px", height: "30px" }} />,
      label: "Dashboard",
      path: "/admin-dashboard",
      permission: null // Everyone can access dashboard
    },
    {
      icon: <img src={IconAnalytics} alt="Analytics" style={{ width: "30px", height: "30px" }} />,
      label: "Analytics",
      path: "/admin-analytics",
      permission: "view_analytics"
    },
    {
      icon: <Users size={30} style={{ color: "white" }} />,
      label: "User Management",
      path: "/admin-users",
      permission: "manage_users"
    },
    {
      icon: <Music size={30} style={{ color: "white" }} />,
      label: "Talent",
      path: "/admin-talent",
      permission: "manage_users"
    },
    {
      icon: <Building size={30} style={{ color: "white" }} />,
      label: "Organisers",
      path: "/admin-organisers",
      permission: "manage_users"
    },
    {
      icon: <img src={IconCalendar} alt="Bookings" style={{ width: "30px", height: "30px" }} />,
      label: "Booking Management",
      path: "/admin-bookings",
      permission: "manage_bookings"
    },
    {
      icon: <Activity size={30} style={{ color: "white" }} />,
      label: "Flow Monitor",
      path: "/admin-flow-monitor",
      permission: "manage_bookings"
    },
    {
      icon: <FileText size={30} style={{ color: "white" }} />,
      label: "Content Management",
      path: "/admin-content",
      permission: "manage_content"
    },
    {
      icon: <List size={30} style={{ color: "white" }} />,
      label: "Audit Logs",
      path: "/admin-audit-logs",
      permission: "view_audit_logs"
    },
    {
      icon: <img src={IconDollar} alt="Financial" style={{ width: "30px", height: "30px" }} />,
      label: "Financial Reports",
      path: "/admin-financial",
      permission: "view_analytics"
    },
    {
      icon: <img src={IconNews} alt="News" style={{ width: "30px", height: "30px" }} />,
      label: "News Administration",
      path: "/admin-news",
      permission: "manage_content"
    },
    {
      icon: <img src={IconCalendar} alt="Calendar" style={{ width: "30px", height: "30px" }} />,
      label: "Calendar",
      path: "/admin-calendar",
      permission: "view_bookings"
    },
    {
      icon: <img src={IconContract} alt="Contracts Vault" style={{ width: "30px", height: "30px" }} />,
      label: "Contracts Vault",
      path: "/admin-contracts-vault",
      permission: "view_bookings"
    },
    {
      icon: <img src={IconSettings} alt="Settings" style={{ width: "30px", height: "30px" }} />,
      label: "System Settings",
      path: "/admin-settings",
      permission: "manage_settings"
    },
    {
      icon: <Settings size={30} style={{ color: "white" }} />,
      label: "Booking Status",
      path: "/admin-booking-status",
      permission: "manage_bookings"
    },
    {
      icon: <img src={IconCalendar} alt="Migration" style={{ width: "30px", height: "30px" }} />,
      label: "Calendar Migration",
      path: "/calendar-migration-test",
      permission: "manage_settings"
    },
    {
      icon: <img src={IconDollar} alt="Escrow" style={{ width: "30px", height: "30px" }} />,
      label: "Escrow Checker",
      path: "/admin-escrow-checker",
      permission: "manage_bookings"
    },
    {
      icon: <FileText size={30} style={{ color: "white" }} />,
      label: "TradeSafe Test Tools",
      path: "/admin-tradesafe-test",
      permission: "manage_bookings"
    },
    {
      icon: <FileText size={30} style={{ color: "white" }} />,
      label: "Gallery Test",
      path: "/admin-gallery-test",
      permission: null // Everyone can access for testing
    },
    {
      icon: <FileText size={30} style={{ color: "white" }} />,
      label: "Profile Templates",
      path: "/admin-profile-templates",
      permission: null // Development access
    },
    {
      icon: <Code size={30} style={{ color: "white" }} />,
      label: "Dev - MediaRow",
      path: "/admin-dev-mediarow",
      permission: null // Development access
    },
    {
      icon: <LayoutGrid size={30} style={{ color: "white" }} />,
      label: "Profile Design Sandbox",
      path: "/admin-profile-sandbox",
      permission: null // Development access
    },
  ];
  
  useEffect(() => {
    const loadPermissions = async () => {
      setLoading(true);
      try {
        if (auth.currentUser) {
          // Check if user is a super admin by email
          const isSuperAdminEmail = auth.currentUser.email === 'justin@gigify.co.za' || 
                                   auth.currentUser.email === 'brendan@gigify.co.za';
          
          if (isSuperAdminEmail) {
            // Super admins see all menu items
            debug.log('AdminSidebar', 'Super admin detected, showing all menu items');
            setAvailableMenuItems(allMenuItems);
          } else {
            // Get all permissions for the current admin user
            const permissions = await getAdminPermissions(auth.currentUser.uid);
            debug.log('AdminSidebar', 'Loaded permissions', { permissions });
            
            // Filter menu items based on permissions
            const filteredItems = allMenuItems.filter(item => {
              // Items with no permission requirement are always shown
              if (!item.permission) return true;
              
              // Show items where the admin has the required permission
              return permissions.includes(item.permission);
            });
            
            setAvailableMenuItems(filteredItems);
          }
        } else {
          // No user logged in, only show dashboard
          setAvailableMenuItems(allMenuItems.filter(item => !item.permission));
        }
      } catch (error) {
        debug.error("Error loading admin permissions:", error);
        // On error, only show dashboard for safety
        setAvailableMenuItems(allMenuItems.filter(item => !item.permission));
      } finally {
        setLoading(false);
      }
    };
    
    loadPermissions();
  }, [auth.currentUser]);
  
  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Dispatch custom event for cache invalidation
      window.dispatchEvent(new Event('gigify-logout'));
      navigate("/admin-login");
      // Close mobile menu if open
      setIsMobileMenuOpen(false);
    } catch (error) {
      debug.error("Error signing out:", error);
    }
  };
  
  const handleNavigation = (path) => {
    // Close mobile menu after navigation
    setIsMobileMenuOpen(false);
  };

  // Check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // If mobile overlay mode, render as prominent overlay
  if (isMobileOpen) {
    return (
      <>
        {/* Mobile Overlay Background */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 998,
            opacity: isMobileMenuOpen ? 1 : 0,
            visibility: isMobileMenuOpen ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease, visibility 0.3s ease'
          }}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        
        {/* Mobile Sidebar */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: isMobileMenuOpen ? 0 : '-300px',
            width: '300px',
            height: '100vh',
            backgroundColor: '#144874',
            zIndex: 999,
            transition: 'left 0.3s ease',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '2px 0 10px rgba(0,0,0,0.2)'
          }}
        >
          <div style={{ ...styles.logoContainer, paddingTop: '20px' }}>
            <img
              src={logoImage}
              alt="Gigify Logo"
              style={styles.logo}
            />
            <div style={styles.adminBadge}>
              <Shield size={16} style={{ marginRight: "5px" }} />
              <span>Admin Panel</span>
            </div>
          </div>
          
          <nav style={styles.nav}>
            {loading ? (
              <div style={styles.loadingIndicator}>Loading...</div>
            ) : (
              availableMenuItems.map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  onClick={handleNavigation}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    fontFamily: 'Montserrat, sans-serif',
                    fontWeight: 'bold',
                    fontSize: '14px',
                    marginBottom: '25px',
                    cursor: 'pointer',
                    color: isActive(item.path) ? '#f85008' : 'white',
                    transition: 'transform 0.2s ease',
                    textDecoration: 'none',
                    paddingLeft: '20px'
                  }}
                >
                  <div style={{ marginRight: "15px" }}>
                    {item.icon}
                  </div>
                  <span>{item.label}</span>
                </Link>
              ))
            )}
          </nav>
          
          <div style={styles.logoutContainer}>
            <button
              onClick={handleLogout}
              style={styles.logoutButton}
            >
              <LogOut size={24} style={{ marginRight: "10px" }} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div 
        className="admin-sidebar desktop-sidebar" 
        style={{
          width: "280px",
          height: "100vh",
          backgroundColor: "#144874",
          color: "#ffffff",
          paddingTop: "20px",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          left: 0,
          top: 0,
          boxShadow: "2px 0 10px rgba(0,0,0,0.2)",
          zIndex: 100,
          overflowY: "auto"
        }}
      >
        <div style={styles.logoContainer}>
          <img
            src={logoImage}
            alt="Gigify Logo"
            style={styles.logo}
          />
          <div style={styles.adminBadge}>
            <Shield size={16} style={{ marginRight: "5px" }} />
            <span>Admin Panel</span>
          </div>
        </div>
        
        <nav style={styles.nav}>
          {loading ? (
            <div style={styles.loadingIndicator}>Loading...</div>
          ) : (
            availableMenuItems.map((item, index) => (
              <Link
                key={index}
                to={item.path}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontFamily: 'Montserrat, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  marginBottom: '30px',
                  cursor: 'pointer',
                  color: isActive(item.path) ? '#f85008' : 'white',
                  transition: 'transform 0.2s ease',
                  textDecoration: 'none',
                  paddingLeft: '20px'
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateX(10px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateX(0)')}
              >
                <div style={{ marginRight: "15px" }}>
                  {item.icon}
                </div>
                <span>{item.label}</span>
              </Link>
            ))
          )}
        </nav>
        
        <div style={styles.logoutContainer}>
          <button
            onClick={handleLogout}
            style={styles.logoutButton}
          >
            <LogOut size={24} style={{ marginRight: "10px" }} />
            <span>Logout</span>
          </button>
        </div>
      </div>
      
      {/* Mobile Hamburger Menu */}
      <nav className={`mobile-floating-nav ${isMobileMenuOpen ? 'expanded' : ''}`}>
        <HamburgerButton
          isOpen={isMobileMenuOpen}
          onClick={handleButtonClick}
          size={24}
          ariaLabel={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          className="mobile-menu-toggle"
        />
        
        <ul className="mobile-nav-items" role="menu">
          {!loading && availableMenuItems.map((item, index) => (
            <li 
              key={index}
              className={isActive(item.path) ? 'active' : ''}
              role="menuitem"
            >
              <Link
                to={item.path}
                onClick={() => setIsMobileMenuOpen(false)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  color: 'inherit',
                  textDecoration: 'none',
                  width: '100%',
                  padding: '12px 20px'
                }}
              >
                <div style={{ marginRight: "12px", flexShrink: 0 }}>
                  {React.cloneElement(item.icon, { 
                    style: { ...item.icon.props.style, width: "20px", height: "20px" } 
                  })}
                </div>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>{item.label}</span>
              </Link>
            </li>
          ))}
          <li role="menuitem">
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                color: 'inherit',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                width: '100%',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              <LogOut size={20} style={{ marginRight: "12px" }} />
              <span>Logout</span>
            </button>
          </li>
        </ul>
      </nav>
      
      <style>
        {`
         /* Mobile and tablet styles */
         @media (max-width: 1200px) {
           .desktop-sidebar {
             display: none !important;
           }
         }
         
         /* Mobile Hamburger Menu - Simplified */
         .mobile-floating-nav {
           position: fixed;
           top: 50%;
           left: 0;
           transform: translateY(-50%);
           z-index: 1000;
           display: none;
         }
         
         @media (max-width: 1200px) {
           .mobile-floating-nav {
             display: block;
           }
         }
         
         .mobile-menu-toggle {
           position: absolute;
           left: 10px;
           top: 50%;
           transform: translateY(-50%);
           background-color: #144874;
           border-radius: 0 50% 50% 0;
           padding: 15px 20px 15px 15px;
           box-shadow: 2px 2px 10px rgba(0,0,0,0.3);
           cursor: pointer;
           transition: all 0.3s ease;
         }
         
         .mobile-floating-nav.expanded .mobile-menu-toggle {
           left: 250px;
         }
         
         .mobile-nav-items {
           position: fixed;
           top: 0;
           left: -250px;
           width: 250px;
           height: 100vh;
           background-color: #144874;
           list-style: none;
           padding: 80px 0 20px 0;
           margin: 0;
           transition: left 0.3s ease;
           overflow-y: auto;
           box-shadow: 2px 0 10px rgba(0,0,0,0.3);
         }
         
         .mobile-floating-nav.expanded .mobile-nav-items {
           left: 0;
         }
         
         .mobile-nav-items li {
           color: white;
           margin-bottom: 5px;
           transition: background-color 0.2s ease;
         }
         
         .mobile-nav-items li:hover {
           background-color: rgba(255,255,255,0.1);
         }
         
         .mobile-nav-items li.active {
           background-color: rgba(248, 80, 8, 0.2);
           border-left: 4px solid #f85008;
         }
         
         .mobile-nav-items li.active a {
           color: #f85008 !important;
         }
         
         /* Touch optimization */
         @media (max-width: 1200px) {
           .mobile-nav-items button {
             min-height: 44px;
             touch-action: manipulation;
           }
         }
         
         /* Animation improvements */
         @media (prefers-reduced-motion: reduce) {
           .mobile-floating-nav,
           .mobile-nav-items,
           .mobile-menu-toggle {
             transition: none;
           }
         }
        `}
      </style>
    </>
  );
};

const styles = {
  logoContainer: {
    textAlign: "center",
    marginBottom: "20px",
    padding: "0 20px",
  },
  logo: {
    width: "180px",
    marginBottom: "10px",
  },
  adminBadge: {
    backgroundColor: "#f85008",
    color: "white",
    padding: "5px 10px",
    borderRadius: "20px",
    display: "inline-flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: "bold",
  },
  nav: {
    flexGrow: 1,
    overflowY: "auto",
    padding: "10px",
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(255,255,255,0.3) transparent"
  },
  logoutContainer: {
    padding: "20px",
    borderTop: "1px solid rgba(255,255,255,0.1)",
  },
  logoutButton: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "12px",
    borderRadius: "10px",
    background: "rgba(255,255,255,0.1)",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    transition: "background-color 0.3s ease",
    fontSize: "16px",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.2)"
    }
  },
  loadingIndicator: {
    textAlign: "center",
    padding: "20px",
    color: "rgba(255,255,255,0.7)",
  },
};

export default AdminSidebar;